import React, { useEffect, useRef, useState } from "react";
import { DialogContainer } from "react-md";
import FontIcon from "react-md/lib/FontIcons";
import EventService from "../../../../../../service/event/EventService";
import CartDashboardUtils from "../../services/CartDashboardUtils";

const DESCRIPTION_WORD_LIMIT = 250;

const wordCount = (s) => {
  if (!s) return 0;
  return s.trim().split(/\s+/).filter(Boolean).length;
};

/**
 * "Mark on hold" reason picker shown from PrescribedDiagnosticsScreen when the
 * reviewer clicks the orange "Mark as on hold" CTA on a PENDING_REVIEW cart.
 *
 * Fetches the dropdown options from `GET /admin/hold-reasons` on mount, lets
 * the reviewer pick one + type a free-text description, and on Continue calls
 * `POST /admin/{cartId}/hold`. Calls `onSuccess(updatedCart)` — the screen
 * then swaps this dialog for MarkOnHoldSuccessDialog.
 *
 * An option carrying `requiresDescription: true` (e.g. "Other reason") makes
 * the description mandatory. The backend enforces this too and 400s without
 * it, so gating Continue here is what turns a generic error toast into an
 * obvious disabled button.
 */
const MarkOnHoldDialog = ({ visible, cartId, onClose, onSuccess }) => {
  const cartDashboardUtils = useRef(new CartDashboardUtils()).current;
  const [reasons, setReasons] = useState([]);
  const [reasonsLoading, setReasonsLoading] = useState(true);
  const [reasonsOpen, setReasonsOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setReasonsLoading(true);
    // Active only — a retired reason must not be offered for a new hold.
    cartDashboardUtils.fetchHoldReasons(
      false,
      (res) => {
        setReasons(Array.isArray(res) ? res : []);
        setReasonsLoading(false);
      },
      (err) => {
        setReasonsLoading(false);
        EventService.emit("showError", {
          message: `Error loading hold reasons: ${err?.message || "Unknown error"}`,
        });
      },
    );
  }, [visible]);

  // Reset local state every time the dialog re-opens so a previous attempt's
  // selection doesn't leak into a fresh attempt.
  useEffect(() => {
    if (visible) {
      setSelectedReason(null);
      setDescription("");
      setReasonsOpen(false);
      setSubmitting(false);
    }
  }, [visible]);

  const descWords = wordCount(description);
  const descriptionRequired = !!selectedReason?.requiresDescription;
  const descriptionMissing = descriptionRequired && description.trim() === "";
  const canContinue = !!selectedReason && !descriptionMissing && !submitting;

  const handleContinue = () => {
    if (!canContinue) return;
    setSubmitting(true);
    cartDashboardUtils.holdCart(
      cartId,
      {
        reason: selectedReason.id,
        description: description.trim(),
      },
      (res) => {
        setSubmitting(false);
        if (onSuccess) onSuccess(res);
      },
      (err) => {
        setSubmitting(false);
        EventService.emit("showError", {
          message: `Failed to mark on hold: ${err?.message || "Unknown error"}`,
        });
      },
    );
  };

  return (
    <DialogContainer
      id="cart-mark-on-hold-dialog"
      visible={visible}
      onHide={onClose}
      focusOnMount={false}
      width={"560px"}
      autosizeContent={true}
      dialogStyle={{ borderRadius: "10px" }}
      modal
    >
      <div className="mhd-container">
        <div className="mhd-header">
          <h2 className="mhd-title">Mark on hold</h2>
          <span className="mhd-close-btn" onClick={onClose}>
            &times;
          </span>
        </div>

        <div className="mhd-body">
          <div className="mhd-label">Reason for marking on hold</div>
          <div className="mhd-select-wrap">
            <button
              type="button"
              className={`mhd-select ${reasonsOpen ? "mhd-select--open" : ""} ${
                !selectedReason ? "mhd-select--placeholder" : ""
              }`}
              onClick={() => !reasonsLoading && setReasonsOpen((v) => !v)}
              disabled={reasonsLoading || submitting}
            >
              <span>
                {reasonsLoading
                  ? "Loading…"
                  : selectedReason
                    ? selectedReason.label
                    : "Select reason"}
              </span>
              <FontIcon style={{ fontSize: "20px", color: "#f97f0f" }}>
                {reasonsOpen ? "expand_less" : "expand_more"}
              </FontIcon>
            </button>

            {reasonsOpen && (
              <div className="mhd-select-menu">
                {reasons.length === 0 ? (
                  <div className="mhd-select-empty">No reasons available</div>
                ) : (
                  reasons.map((r) => (
                    <div
                      key={r.id}
                      className={`mhd-select-option ${
                        selectedReason?.id === r.id
                          ? "mhd-select-option--selected"
                          : ""
                      }`}
                      onClick={() => {
                        setSelectedReason(r);
                        setReasonsOpen(false);
                      }}
                    >
                      {r.label}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="mhd-label" style={{ marginTop: "20px" }}>
            Please describe the reason for marking on hold
            {descriptionRequired && <span className="mhd-required"> *</span>}
          </div>
          <div className="mhd-textarea-wrap">
            <textarea
              className="mhd-textarea"
              placeholder={
                descriptionRequired
                  ? "Required for the selected reason"
                  : "Type here…"
              }
              value={description}
              onChange={(e) => {
                const next = e.target.value;
                if (wordCount(next) <= DESCRIPTION_WORD_LIMIT) {
                  setDescription(next);
                }
              }}
              disabled={submitting}
              rows={3}
            />
            <div className="mhd-textarea-count">
              {descWords}/{DESCRIPTION_WORD_LIMIT} words
            </div>
          </div>
        </div>

        <div className="mhd-footer">
          <button
            type="button"
            className="mhd-btn mhd-btn--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Go back
          </button>
          <button
            type="button"
            className={`mhd-btn mhd-btn--primary ${
              !canContinue ? "mhd-btn--disabled" : ""
            }`}
            onClick={handleContinue}
            disabled={!canContinue}
          >
            {submitting ? "Saving…" : "Continue"}
          </button>
        </div>
      </div>
    </DialogContainer>
  );
};

export default MarkOnHoldDialog;
